import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress } from '@mui/material';
import { createCategory, updateCategory } from './.request';
import { useAuth } from '../context/AuthContext';
import { IMAGE_URL } from '../constants/Consts';

const CreateCategoryModal = ({ open, onClose, categoryToEdit, onSuccess }) => {
  const { authToken } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (categoryToEdit) {
      setTitle(categoryToEdit.title || '');
      setDescription(categoryToEdit.description || '');
      if (categoryToEdit.cover_image) {
        setPreview(`${IMAGE_URL}munasibaatImages/${categoryToEdit.cover_image}`);
      } else {
        setPreview(null);
      }
    } else {
      setTitle('');
      setDescription('');
      setCoverImage(null);
      setPreview(null);
    }
    setError('');
  }, [categoryToEdit, open]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImage(reader.result);
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setLoading(true);
    setError('');

    const payload = {
      title,
      description,
      cover_image: coverImage,
    };

    let res;
    if (categoryToEdit) {
      res = await updateCategory(authToken, categoryToEdit.id, payload);
    } else {
      res = await createCategory(authToken, payload);
    }

    setLoading(false);
    if (res.success) {
      onSuccess();
      onClose();
    } else {
      setError(res.errors ? res.errors.join(', ') : 'Operation failed');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{categoryToEdit ? 'Edit Munasibat Category' : 'Create Munasibat Category'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <div className="p-3 mb-3 bg-red-100 text-red-700 rounded">{error}</div>}
          <TextField
            label="Category Title (e.g. Muharram, Ayyam e Fatimiya)"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
          />
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            {preview && (
              <div className="mt-3">
                <img src={preview} alt="Preview" className="w-full h-40 object-cover rounded border" />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">Cancel</Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : categoryToEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCategoryModal;
